import { Link } from 'react-router-dom';
import Navbar from './home/components/Navbar';
import Footer from './home/components/Footer';
import RegisterLink from '../components/RegisterLink';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageSeo } from '../hooks/usePageSeo';
import { LinkedText } from '../components/LinkedText';

type FaqItem = {
  en_q: string;
  bm_q: string;
  en_a: string[];
  bm_a: string[];
  en_points?: string[];
  bm_points?: string[];
};

type FaqSection = {
  id: string;
  en_heading: string;
  bm_heading: string;
  en_intro: string[];
  bm_intro: string[];
  items: FaqItem[];
};

const faqSections: FaqSection[] = [
  {
    id: 'about-mrcuci',
    en_heading: 'About MrCuci Malaysia Online Casino',
    bm_heading: 'Tentang Kasino Dalam Talian MrCuci Malaysia',
    en_intro: [
      'MrCuci is an online casino platform serving eligible Malaysian players with access to several categories of online casino entertainment. Instead of focusing on one format, the platform brings together slot games, live casino tables, jackpots, fishing games and other supported titles in one place.',
      'For Malaysian players, practical considerations such as MYR transactions, mobile compatibility, payment availability, customer support and clear promotion requirements can be as important as the games themselves.',
    ],
    bm_intro: [
      'MrCuci ialah platform kasino dalam talian untuk pemain Malaysia yang layak, dengan akses kepada beberapa kategori hiburan kasino. Platform ini menghimpunkan slot, meja kasino langsung, jackpot, permainan memancing dan tajuk lain yang disokong dalam satu tempat.',
      'Bagi pemain Malaysia, transaksi MYR, keserasian mudah alih, pilihan pembayaran, sokongan pelanggan dan syarat promosi yang jelas sama pentingnya dengan pilihan permainan.',
    ],
    items: [
      {
        en_q: 'What is MrCuci Malaysia?',
        bm_q: 'Apakah MrCuci Malaysia?',
        en_a: [
          'MrCuci is an online casino platform where eligible adult players can register an account and access supported real-money casino games.',
          'Its game categories include online slots, live dealer casino games, progressive jackpot games, fishing titles and other casino entertainment supplied through the platform. Availability can change as providers update their catalogues.',
        ],
        bm_a: [
          'MrCuci ialah platform kasino dalam talian tempat pemain dewasa yang layak boleh mendaftar akaun dan mengakses permainan kasino wang sebenar yang disokong.',
          'Kategori permainan termasuk slot dalam talian, kasino peniaga langsung, jackpot progresif, permainan memancing dan hiburan kasino lain. Ketersediaan boleh berubah apabila katalog pembekal dikemas kini.',
        ],
      },
      {
        en_q: 'Who can register for a MrCuci account?',
        bm_q: 'Siapa yang boleh mendaftar akaun MrCuci?',
        en_a: [
          'MrCuci is intended only for adults aged 18 and above who meet the legal requirements that apply to them. Online gambling rules can differ by location, so users remain responsible for checking their own eligibility before registering or playing for real money.',
          'Registration details should be accurate and belong to the account holder. Correct information can be needed later for account recovery, payment verification and withdrawal reviews.',
        ],
        bm_a: [
          'MrCuci hanya ditujukan kepada orang dewasa berumur 18 tahun ke atas yang memenuhi keperluan undang-undang yang terpakai. Peraturan perjudian dalam talian berbeza mengikut lokasi, maka pengguna bertanggungjawab menyemak kelayakan sendiri.',
          'Butiran pendaftaran mestilah tepat dan milik pemegang akaun kerana maklumat ini mungkin diperlukan untuk pemulihan akaun, pengesahan pembayaran dan semakan pengeluaran.',
        ],
      },
      {
        en_q: 'Why should I use the official MrCuci website?',
        bm_q: 'Mengapa saya perlu menggunakan laman web rasmi MrCuci?',
        en_a: [
          'Using the official website reduces the risk of entering login or payment information on an unrelated or impersonating website. Check the domain before submitting account credentials, verification codes or financial details.',
          'Be cautious with links received through unknown messages, pop-ups, advertisements or unofficial groups. If a link looks unusual, open the official site directly instead.',
        ],
        bm_a: [
          'Menggunakan laman rasmi mengurangkan risiko memasukkan maklumat log masuk atau pembayaran pada laman palsu. Semak domain sebelum menghantar kelayakan akaun, kod pengesahan atau butiran kewangan.',
          'Berhati-hati dengan pautan daripada mesej, iklan, pop-up atau kumpulan tidak rasmi. Jika pautan kelihatan mencurigakan, buka laman rasmi secara terus.',
        ],
      },
    ],
  },
  {
    id: 'registration-login',
    en_heading: 'MrCuci Account Registration & Login',
    bm_heading: 'Pendaftaran Akaun & Log Masuk MrCuci',
    en_intro: [
      'Creating and maintaining a secure account is the first step before using real-money features. Accurate information and safe login habits help prevent avoidable account, payment and verification problems.',
    ],
    bm_intro: [
      'Membuat dan mengekalkan akaun yang selamat ialah langkah pertama sebelum menggunakan ciri wang sebenar. Maklumat tepat dan tabiat log masuk selamat membantu mengelakkan masalah akaun, pembayaran dan pengesahan.',
    ],
    items: [
      {
        en_q: 'How do I register a MrCuci Malaysia account?',
        bm_q: 'Bagaimana saya mendaftar akaun MrCuci Malaysia?',
        en_a: [
          'Visit the official MrCuci website, select Register and complete the requested information. Review your name and contact details before submitting the form because incorrect information may make account recovery or transaction verification more difficult.',
          'After registration, log in to browse games, promotions, payment options and account tools. Complete any verification request shown in your account before making larger transactions.',
        ],
        bm_a: [
          'Lawati laman rasmi MrCuci, pilih Daftar dan lengkapkan maklumat yang diminta. Semak nama serta butiran hubungan sebelum menghantar borang kerana maklumat salah boleh menyukarkan pemulihan akaun atau pengesahan transaksi.',
          'Selepas mendaftar, log masuk untuk melihat permainan, promosi, pembayaran dan alat akaun. Lengkapkan permintaan pengesahan yang dipaparkan sebelum membuat transaksi lebih besar.',
        ],
      },
      {
        en_q: 'What should I do if I cannot log in?',
        bm_q: 'Apakah yang perlu dilakukan jika saya tidak boleh log masuk?',
        en_a: [
          'Check that your username, phone number and password were entered correctly. If you forgot your password, use the available recovery process or contact official customer support.',
          'Avoid repeatedly guessing passwords because this can trigger a temporary security restriction. Never give your password or one-time verification code to a person claiming they need it to restore the account.',
        ],
        bm_a: [
          'Semak nama pengguna, nombor telefon dan kata laluan. Jika terlupa kata laluan, gunakan proses pemulihan atau hubungi sokongan pelanggan rasmi.',
          'Elakkan meneka kata laluan berulang kali kerana ia boleh mencetuskan sekatan keselamatan sementara. Jangan berikan kata laluan atau kod sekali guna kepada sesiapa.',
        ],
      },
      {
        en_q: 'Can I have multiple MrCuci accounts?',
        bm_q: 'Bolehkah saya mempunyai beberapa akaun MrCuci?',
        en_a: [
          'Players should maintain one account and follow the account rules in the Terms and Conditions. Multiple accounts can create problems with identity checks, bonus eligibility and withdrawals.',
          'If you think an old account still exists, contact support before creating another one. The team can advise on account recovery or the correct next step.',
        ],
        bm_a: [
          'Pemain perlu mengekalkan satu akaun dan mematuhi Terma dan Syarat. Akaun berganda boleh menimbulkan masalah pada semakan identiti, kelayakan bonus dan pengeluaran.',
          'Jika akaun lama mungkin masih wujud, hubungi sokongan sebelum membuat akaun baharu untuk mendapatkan langkah yang betul.',
        ],
      },
    ],
  },
  {
    id: 'casino-games',
    en_heading: 'Online Casino Games at MrCuci Malaysia',
    bm_heading: 'Permainan Kasino Dalam Talian di MrCuci Malaysia',
    en_intro: [
      'Game selection is an important consideration when comparing online casinos. MrCuci provides different casino categories so players can browse by preferred format instead of being limited to a single type of game.',
      'Individual games have different rules, volatility, table limits and bonus mechanics. Read the information inside a game before placing a real-money wager.',
    ],
    bm_intro: [
      'Pilihan permainan ialah pertimbangan penting apabila membandingkan kasino dalam talian. MrCuci menyediakan kategori berbeza supaya pemain boleh melayari mengikut format pilihan.',
      'Setiap permainan mempunyai peraturan, volatiliti, had meja dan mekanik bonus berbeza. Baca maklumat permainan sebelum membuat pertaruhan wang sebenar.',
    ],
    items: [
      {
        en_q: 'What casino games are available at MrCuci?',
        bm_q: 'Apakah permainan kasino yang tersedia di MrCuci?',
        en_a: [
          'The catalogue may include online slots, live casino games, Baccarat, Blackjack, Roulette, progressive jackpots, fishing and arcade-style games, and other supported casino titles.',
          'The exact selection can change as games and providers are added, removed or updated. Visit the Games page or lobby for the current catalogue.',
        ],
        bm_a: [
          'Katalog boleh merangkumi slot dalam talian, kasino langsung, Baccarat, Blackjack, Roulette, jackpot progresif, permainan memancing dan arked, serta tajuk lain yang disokong.',
          'Pilihan boleh berubah apabila permainan atau pembekal ditambah, dikeluarkan atau dikemas kini. Semak halaman Permainan atau lobi untuk katalog semasa.',
        ],
        en_points: ['Online slots', 'Live dealer tables', 'Progressive jackpots', 'Fishing and arcade games'],
        bm_points: ['Slot dalam talian', 'Meja peniaga langsung', 'Jackpot progresif', 'Permainan memancing dan arked'],
      },
      {
        en_q: 'Does MrCuci Malaysia have online slot games?',
        bm_q: 'Adakah MrCuci Malaysia mempunyai permainan slot dalam talian?',
        en_a: [
          'Yes. Slots form a significant part of the game selection and vary in theme, reel format, volatility, paylines, bonus features and provider.',
          'Slot outcomes involve chance. Previous spins do not predict or guarantee the next result, so choose a stake within your entertainment budget.',
        ],
        bm_a: [
          'Ya. Slot merupakan sebahagian besar pilihan permainan dan berbeza dari segi tema, format reel, volatiliti, paylines, ciri bonus serta pembekal.',
          'Keputusan slot bergantung pada peluang. Putaran lalu tidak meramal atau menjamin keputusan seterusnya, jadi pilih pertaruhan mengikut bajet hiburan.',
        ],
      },
      {
        en_q: 'Are live casino games available?',
        bm_q: 'Adakah permainan kasino langsung tersedia?',
        en_a: [
          'Supported providers may offer live dealer games through real-time video streaming. Depending on availability, players can find Baccarat, Blackjack, Roulette and other table formats.',
          'Table limits, side bets and game procedures differ. Check the rules and minimum wager displayed before joining a table.',
        ],
        bm_a: [
          'Pembekal yang disokong boleh menawarkan permainan peniaga langsung melalui video masa nyata, termasuk Baccarat, Blackjack, Roulette dan format meja lain.',
          'Had meja, pertaruhan sampingan dan prosedur berbeza. Semak peraturan serta pertaruhan minimum sebelum menyertai meja.',
        ],
      },
      {
        en_q: 'How do progressive jackpot games work?',
        bm_q: 'Bagaimana permainan jackpot progresif berfungsi?',
        en_a: [
          'A progressive jackpot can grow as qualifying bets are placed until the relevant winning condition occurs. Rules, qualifying stakes and prize structures depend on the individual title.',
          'A large displayed jackpot is not a promise or guarantee of winning. Review the paytable and jackpot rules inside the game.',
        ],
        bm_a: [
          'Jackpot progresif boleh meningkat apabila pertaruhan layak dibuat sehingga syarat kemenangan berlaku. Peraturan, pertaruhan layak dan struktur hadiah bergantung pada tajuk permainan.',
          'Jackpot besar yang dipaparkan bukan janji kemenangan. Semak paytable dan peraturan jackpot dalam permainan.',
        ],
      },
    ],
  },
  {
    id: 'bonuses-promotions',
    en_heading: 'MrCuci Bonuses & Promotions',
    bm_heading: 'Bonus & Promosi MrCuci',
    en_intro: [
      'Casino promotions can provide extra play value, but the headline amount does not explain the complete offer. Turnover, minimum deposits, eligible games, campaign periods and withdrawal conditions should all be reviewed before claiming.',
    ],
    bm_intro: [
      'Promosi kasino boleh memberikan nilai permainan tambahan, tetapi jumlah utama tidak menerangkan keseluruhan tawaran. Turnover, deposit minimum, permainan layak, tempoh kempen dan syarat pengeluaran perlu disemak sebelum menuntut.',
    ],
    items: [
      {
        en_q: 'What bonuses are currently featured at MrCuci?',
        bm_q: 'Apakah bonus yang sedang ditampilkan di MrCuci?',
        en_a: [
          'Current featured offers include New Member Register Free RM88, Welcome Bonus 50%, Deposit RM200 Free RM200 and Monthly Competition Deposit Ranking RM1088.',
          'Campaigns can change, so refer to the current Bonus page and in-account promotion details instead of relying on an old screenshot or advertisement.',
        ],
        bm_a: [
          'Tawaran semasa termasuk Ahli Baharu Daftar Percuma RM88, Bonus Selamat Datang 50%, Deposit RM200 Percuma RM200 dan Pertandingan Bulanan Kedudukan Deposit RM1088.',
          'Kempen boleh berubah, maka rujuk halaman Bonus dan butiran promosi dalam akaun, bukan tangkapan skrin atau iklan lama.',
        ],
      },
      {
        en_q: 'How do I claim a MrCuci promotion?',
        bm_q: 'Bagaimana saya menuntut promosi MrCuci?',
        en_a: [
          'The process depends on the promotion. Some offers require a qualifying deposit, some must be selected in the account, and others can be credited after specified conditions are met.',
          'Read the complete requirements before depositing specifically for a campaign. Check the claim period, minimum deposit, eligible games and any maximum withdrawal rule.',
        ],
        bm_a: [
          'Proses bergantung pada promosi. Sesetengah tawaran memerlukan deposit layak, perlu dipilih dalam akaun, atau dikreditkan selepas syarat tertentu dipenuhi.',
          'Baca syarat penuh sebelum deposit untuk kempen. Semak tempoh tuntutan, deposit minimum, permainan layak dan peraturan pengeluaran maksimum.',
        ],
      },
      {
        en_q: 'What is a wagering or turnover requirement?',
        bm_q: 'Apakah keperluan wagering atau turnover?',
        en_a: [
          'A turnover requirement states how much qualifying play must be completed before bonus-related funds or winnings become eligible for withdrawal. Receiving a bonus does not necessarily mean it can immediately be withdrawn as cash.',
          'Calculations vary between promotions and certain games may contribute differently or not at all. Use the formula in the individual promotion terms.',
        ],
        bm_a: [
          'Keperluan turnover menyatakan jumlah permainan layak yang perlu diselesaikan sebelum dana atau kemenangan berkaitan bonus boleh dikeluarkan. Menerima bonus tidak bermakna ia boleh terus dikeluarkan sebagai tunai.',
          'Pengiraan berbeza antara promosi dan sesetengah permainan mungkin menyumbang secara berbeza atau tidak langsung. Gunakan formula dalam terma promosi berkenaan.',
        ],
      },
      {
        en_q: 'Why should I read bonus terms before claiming?',
        bm_q: 'Mengapa saya perlu membaca terma bonus sebelum menuntut?',
        en_a: [
          'Bonus terms explain whether an offer fits your preferred games, deposit amount and withdrawal expectations. Understanding the conditions before claiming helps prevent confusion later.',
        ],
        bm_a: [
          'Terma bonus menerangkan sama ada tawaran sesuai dengan permainan pilihan, jumlah deposit dan jangkaan pengeluaran anda. Memahami syarat sebelum menuntut membantu mengelakkan kekeliruan.',
        ],
        en_points: ['Minimum deposit', 'Eligible games', 'Turnover formula', 'Promotion period', 'Maximum bonus or withdrawal', 'Payment restrictions'],
        bm_points: ['Deposit minimum', 'Permainan layak', 'Formula turnover', 'Tempoh promosi', 'Bonus atau pengeluaran maksimum', 'Sekatan pembayaran'],
      },
    ],
  },
  {
    id: 'myr-deposits',
    en_heading: 'MYR Deposits at MrCuci Malaysia',
    bm_heading: 'Deposit MYR di MrCuci Malaysia',
    en_intro: [
      'Before depositing, confirm the payment methods and transaction limits currently shown in your account. Availability can change according to account status, maintenance or payment-provider updates.',
    ],
    bm_intro: [
      'Sebelum deposit, sahkan kaedah pembayaran dan had transaksi yang dipaparkan dalam akaun. Ketersediaan boleh berubah mengikut status akaun, penyelenggaraan atau kemas kini pembekal pembayaran.',
    ],
    items: [
      {
        en_q: 'Can I deposit using Malaysian Ringgit?',
        bm_q: 'Bolehkah saya deposit menggunakan Ringgit Malaysia?',
        en_a: [
          'Where MYR transactions are supported, Malaysian players can view and manage casino transactions in a familiar currency without manually converting every amount.',
          'Check the cashier before confirming a payment to verify the currency, minimum amount and current processing instructions.',
        ],
        bm_a: [
          'Apabila transaksi MYR disokong, pemain Malaysia boleh melihat dan mengurus transaksi dalam mata wang yang biasa tanpa menukar setiap jumlah secara manual.',
          'Semak cashier sebelum mengesahkan pembayaran untuk melihat mata wang, jumlah minimum dan arahan pemprosesan semasa.',
        ],
      },
      {
        en_q: 'What payment methods does MrCuci accept?',
        bm_q: 'Apakah kaedah pembayaran yang diterima MrCuci?',
        en_a: [
          'Available options may include FPX online banking, MEPS, Touch n\' Go eWallet, Grab Pay, Digi, Hotlink and other supported local channels. The list can change, so the deposit screen in your account is the most relevant source.',
          'Use a payment method registered in your own name and follow the exact reference or transfer instructions shown.',
        ],
        bm_a: [
          'Pilihan boleh termasuk perbankan FPX, MEPS, Touch n\' Go eWallet, Grab Pay, Digi, Hotlink dan saluran tempatan lain. Senarai boleh berubah, jadi skrin deposit dalam akaun ialah rujukan paling relevan.',
          'Gunakan kaedah pembayaran atas nama sendiri dan ikut arahan rujukan atau pemindahan yang dipaparkan.',
        ],
      },
      {
        en_q: 'What should I check before making a deposit?',
        bm_q: 'Apakah yang perlu disemak sebelum membuat deposit?',
        en_a: [
          'Confirm every transaction detail before payment. Also decide on a personal entertainment budget and do not deposit money needed for rent, bills, food, debt payments or other essentials.',
        ],
        bm_a: [
          'Sahkan setiap butiran transaksi sebelum pembayaran. Tetapkan bajet hiburan peribadi dan jangan deposit wang yang diperlukan untuk sewa, bil, makanan, hutang atau keperluan lain.',
        ],
        en_points: ['Deposit amount', 'Payment method', 'Account details', 'Transaction limits', 'Promotion selection and conditions'],
        bm_points: ['Jumlah deposit', 'Kaedah pembayaran', 'Butiran akaun', 'Had transaksi', 'Pilihan dan syarat promosi'],
      },
      {
        en_q: 'What should I do if my deposit does not appear?',
        bm_q: 'Apakah yang perlu dilakukan jika deposit tidak muncul?',
        en_a: [
          'Confirm that the payment completed successfully and check whether the transaction is still processing. Do not immediately repeat the same payment because this may create a duplicate deposit.',
          'If the balance remains unchanged beyond the expected period, contact support with the amount, time, payment method and transaction reference. Do not publish full financial details in a public channel.',
        ],
        bm_a: [
          'Sahkan pembayaran berjaya dan semak sama ada transaksi masih diproses. Jangan terus mengulangi pembayaran yang sama kerana ia boleh menghasilkan deposit berganda.',
          'Jika baki masih tidak berubah, hubungi sokongan dengan jumlah, masa, kaedah pembayaran dan rujukan transaksi. Jangan siarkan butiran kewangan penuh secara terbuka.',
        ],
      },
    ],
  },
  {
    id: 'withdrawal-guide',
    en_heading: 'MrCuci Withdrawal Guide',
    bm_heading: 'Panduan Pengeluaran MrCuci',
    en_intro: [
      'Withdrawal processing can depend on the selected method, account verification, transaction review, provider availability and banking conditions. A single processing time should not be assumed for every request.',
    ],
    bm_intro: [
      'Pemprosesan pengeluaran bergantung pada kaedah, pengesahan akaun, semakan transaksi, ketersediaan pembekal dan keadaan perbankan. Satu tempoh pemprosesan tidak boleh dianggap sama untuk semua permintaan.',
    ],
    items: [
      {
        en_q: 'How do I withdraw from MrCuci?',
        bm_q: 'Bagaimana saya membuat pengeluaran dari MrCuci?',
        en_a: [
          'Log in, open the withdrawal section, choose a supported method and enter the requested amount and payment information. Review the details carefully before confirming.',
          'The account may need to meet verification, minimum withdrawal and promotion conditions before the request can be completed.',
        ],
        bm_a: [
          'Log masuk, buka bahagian pengeluaran, pilih kaedah yang disokong dan masukkan jumlah serta maklumat pembayaran. Semak butiran dengan teliti sebelum mengesahkan.',
          'Akaun mungkin perlu memenuhi syarat pengesahan, pengeluaran minimum dan promosi sebelum permintaan boleh diselesaikan.',
        ],
      },
      {
        en_q: 'How long does a MrCuci withdrawal take?',
        bm_q: 'Berapa lama pengeluaran MrCuci mengambil masa?',
        en_a: [
          'Processing time varies according to the withdrawal method, account status, verification requirements, transaction volume and banking or e-wallet processing.',
          'For the most accurate information about a specific request, check its status in your account or contact support with the transaction reference.',
        ],
        bm_a: [
          'Tempoh pemprosesan berbeza mengikut kaedah pengeluaran, status akaun, pengesahan, jumlah transaksi dan pemprosesan bank atau e-dompet.',
          'Untuk maklumat paling tepat tentang permintaan tertentu, semak status dalam akaun atau hubungi sokongan dengan rujukan transaksi.',
        ],
      },
      {
        en_q: 'Why is my withdrawal pending?',
        bm_q: 'Mengapa pengeluaran saya masih pending?',
        en_a: [
          'A pending status can mean that the request is undergoing normal processing, account review or payment verification. It does not automatically mean the withdrawal has failed.',
          'Check for a request for additional documents or corrected payment information. Contact support if the transaction remains pending beyond the period shown by the platform.',
        ],
        bm_a: [
          'Status pending boleh bermakna permintaan sedang melalui pemprosesan biasa, semakan akaun atau pengesahan pembayaran. Ia tidak semestinya bermakna pengeluaran gagal.',
          'Semak jika terdapat permintaan dokumen tambahan atau pembetulan maklumat pembayaran. Hubungi sokongan jika transaksi melebihi tempoh yang dipaparkan.',
        ],
      },
      {
        en_q: 'Can bonus conditions affect a withdrawal?',
        bm_q: 'Bolehkah syarat bonus menjejaskan pengeluaran?',
        en_a: [
          'Yes. If funds are linked to a promotion, turnover, eligible-game or maximum-withdrawal conditions may need to be met first. Withdrawing early can also affect an active bonus under some campaign rules.',
          'Review the promotion status and terms before submitting a withdrawal, especially if the account contains bonus funds.',
        ],
        bm_a: [
          'Ya. Jika dana berkaitan promosi, syarat turnover, permainan layak atau pengeluaran maksimum mungkin perlu dipenuhi. Pengeluaran awal juga boleh menjejaskan bonus aktif bagi sesetengah kempen.',
          'Semak status dan terma promosi sebelum membuat pengeluaran, terutama jika akaun mengandungi dana bonus.',
        ],
      },
    ],
  },
  {
    id: 'mobile-gaming',
    en_heading: 'Playing MrCuci on Mobile',
    bm_heading: 'Bermain MrCuci pada Mudah Alih',
    en_intro: [
      'Mobile access lets players manage an account and open supported games without relying on a desktop computer. Performance can vary according to the device, browser, network and individual game.',
    ],
    bm_intro: [
      'Akses mudah alih membolehkan pemain mengurus akaun dan membuka permainan tanpa bergantung pada komputer desktop. Prestasi berbeza mengikut peranti, pelayar, rangkaian dan permainan.',
    ],
    items: [
      {
        en_q: 'Can I play MrCuci on my smartphone?',
        bm_q: 'Bolehkah saya bermain MrCuci pada telefon pintar?',
        en_a: [
          'MrCuci can be accessed through compatible mobile browsers. Players can log in, browse games, check promotions and use supported account functions from a phone.',
          'For smoother performance, use an updated browser, close unnecessary background apps and connect through a stable private network.',
        ],
        bm_a: [
          'MrCuci boleh diakses melalui pelayar mudah alih yang serasi. Pemain boleh log masuk, melayari permainan, menyemak promosi dan menggunakan fungsi akaun dari telefon.',
          'Untuk prestasi lebih lancar, gunakan pelayar terkini, tutup aplikasi latar yang tidak perlu dan gunakan rangkaian peribadi yang stabil.',
        ],
      },
      {
        en_q: 'Does MrCuci work on Android and iPhone?',
        bm_q: 'Adakah MrCuci berfungsi pada Android dan iPhone?',
        en_a: [
          'The responsive website is intended to support common modern Android and iOS devices. Screen size, operating-system version and browser compatibility can affect individual games.',
          'If a game does not load, update the browser, refresh the page and try another supported connection before contacting support.',
        ],
        bm_a: [
          'Laman responsif direka untuk menyokong peranti Android dan iOS moden. Saiz skrin, versi sistem dan keserasian pelayar boleh mempengaruhi permainan tertentu.',
          'Jika permainan tidak dimuatkan, kemas kini pelayar, refresh halaman dan cuba sambungan lain sebelum menghubungi sokongan.',
        ],
      },
      {
        en_q: 'Do I need to install a MrCuci app?',
        bm_q: 'Adakah saya perlu memasang aplikasi MrCuci?',
        en_a: [
          'A separate app is not necessary when the required games and account features are available through the mobile website.',
          'If software is offered, follow instructions only from an official MrCuci channel. Avoid unknown APK files or applications promoted through unverified third-party links.',
        ],
        bm_a: [
          'Aplikasi berasingan tidak diperlukan apabila permainan dan ciri akaun tersedia melalui laman mudah alih.',
          'Jika perisian ditawarkan, ikut arahan daripada saluran rasmi MrCuci sahaja. Elakkan fail APK atau aplikasi daripada pautan pihak ketiga yang tidak disahkan.',
        ],
      },
    ],
  },
  {
    id: 'security-support',
    en_heading: 'Account Security, Privacy & Customer Support',
    bm_heading: 'Keselamatan Akaun, Privasi & Sokongan Pelanggan',
    en_intro: [
      'Online casino accounts can contain personal and payment information. Platform safeguards are important, but players must also use secure passwords, verify links and report suspicious activity quickly.',
    ],
    bm_intro: [
      'Akaun kasino dalam talian boleh mengandungi maklumat peribadi dan pembayaran. Perlindungan platform penting, tetapi pemain juga perlu menggunakan kata laluan selamat, mengesahkan pautan dan melaporkan aktiviti mencurigakan dengan segera.',
    ],
    items: [
      {
        en_q: 'How can I keep my MrCuci account secure?',
        bm_q: 'Bagaimana saya memastikan akaun MrCuci selamat?',
        en_a: [
          'Use a strong, unique password and keep login information private. Check the domain before signing in, avoid unknown public devices, log out from shared devices and never share verification codes.',
          'Contact official support immediately if you receive an unexpected password-reset message or notice unfamiliar account activity.',
        ],
        bm_a: [
          'Gunakan kata laluan yang kuat dan unik serta rahsiakan maklumat log masuk. Semak domain, elakkan peranti awam, log keluar dari peranti dikongsi dan jangan kongsi kod pengesahan.',
          'Hubungi sokongan rasmi segera jika menerima mesej reset kata laluan yang tidak diminta atau melihat aktiviti tidak dikenali.',
        ],
      },
      {
        en_q: 'What should I do if I notice an unfamiliar transaction?',
        bm_q: 'Apakah yang perlu dilakukan jika terdapat transaksi tidak dikenali?',
        en_a: [
          'Review recent account activity, change your password and contact support as soon as possible. Provide the transaction type, approximate time and amount, but never send your full password.',
          'Support may restrict certain account actions temporarily while ownership and transaction details are reviewed.',
        ],
        bm_a: [
          'Semak aktiviti akaun, tukar kata laluan dan hubungi sokongan secepat mungkin. Berikan jenis transaksi, masa anggaran dan jumlah, tetapi jangan hantar kata laluan penuh.',
          'Sokongan mungkin menyekat tindakan akaun tertentu sementara pemilikan dan butiran transaksi disemak.',
        ],
      },
      {
        en_q: 'When should I contact MrCuci support?',
        bm_q: 'Bilakah saya perlu menghubungi sokongan MrCuci?',
        en_a: [
          'Contact support for account-specific issues such as login problems, unexplained transaction status, verification questions, a promotion dispute or a technical issue affecting a completed wager.',
          'General game rules and promotion conditions should be reviewed first because the answer may already be displayed on the relevant page.',
        ],
        bm_a: [
          'Hubungi sokongan untuk masalah khusus akaun seperti log masuk, status transaksi tidak jelas, pengesahan, pertikaian promosi atau isu teknikal pada pertaruhan yang selesai.',
          'Semak peraturan permainan dan syarat promosi terlebih dahulu kerana jawapan mungkin sudah dipaparkan pada halaman berkaitan.',
        ],
      },
      {
        en_q: 'What information should I provide to support?',
        bm_q: 'Apakah maklumat yang perlu diberikan kepada sokongan?',
        en_a: [
          'Provide your member ID and details relevant to the issue, such as transaction type, amount, approximate time, game, provider or promotion. Screenshots can help when they do not expose passwords or full payment credentials.',
          'Never send your password, one-time code or complete card and banking credentials to a support agent.',
        ],
        bm_a: [
          'Berikan ID ahli serta butiran berkaitan seperti jenis transaksi, jumlah, masa, permainan, pembekal atau promosi. Tangkapan skrin boleh membantu jika tidak mendedahkan kata laluan atau butiran pembayaran penuh.',
          'Jangan hantar kata laluan, kod sekali guna atau kelayakan perbankan lengkap kepada ejen sokongan.',
        ],
      },
    ],
  },
  {
    id: 'responsible-gaming',
    en_heading: 'Responsible Gaming at MrCuci Malaysia',
    bm_heading: 'Permainan Bertanggungjawab di MrCuci Malaysia',
    en_intro: [
      'Online casino gaming should be treated as paid entertainment, not a way to generate guaranteed income. Casino games involve financial risk, and setting personal money and time limits before playing is important.',
    ],
    bm_intro: [
      'Permainan kasino dalam talian perlu dianggap sebagai hiburan berbayar, bukan cara menjana pendapatan terjamin. Permainan kasino melibatkan risiko kewangan, maka had wang dan masa perlu ditetapkan sebelum bermain.',
    ],
    items: [
      {
        en_q: 'How can I gamble more responsibly?',
        bm_q: 'Bagaimana saya boleh bermain dengan lebih bertanggungjawab?',
        en_a: [
          'Decide how much money and time you can spend before starting. Do not increase the budget to recover a loss, and take regular breaks to keep track of time and spending.',
          'Avoid gambling when distressed, tired or under the influence of alcohol. Never use money reserved for essential expenses.',
        ],
        bm_a: [
          'Tentukan jumlah wang dan masa sebelum bermula. Jangan tambah bajet untuk mendapatkan semula kerugian dan berehat secara berkala untuk memantau masa serta perbelanjaan.',
          'Elakkan bermain ketika tertekan, letih atau di bawah pengaruh alkohol. Jangan gunakan wang untuk perbelanjaan penting.',
        ],
      },
      {
        en_q: 'What are signs that I should stop playing?',
        bm_q: 'Apakah tanda bahawa saya perlu berhenti bermain?',
        en_a: [
          'Take a break if gambling begins to affect household spending, causes borrowing, interferes with work or relationships, leads to repeated loss chasing, creates emotional distress or consumes much more time than planned.',
          'Casino games should remain entertainment. When they stop feeling like entertainment, request limits, cooling-off or self-exclusion support.',
        ],
        bm_a: [
          'Berehat jika perjudian menjejaskan perbelanjaan rumah, menyebabkan pinjaman, mengganggu kerja atau hubungan, mendorong mengejar kerugian, menimbulkan tekanan atau mengambil masa jauh lebih lama daripada dirancang.',
          'Permainan kasino perlu kekal sebagai hiburan. Jika tidak lagi terasa begitu, minta had, tempoh bertenang atau pengecualian diri.',
        ],
        en_points: ['Borrowing money to play', 'Chasing losses', 'Hiding play from family', 'Ignoring bills or responsibilities', 'Feeling unable to stop'],
        bm_points: ['Meminjam wang untuk bermain', 'Mengejar kerugian', 'Menyembunyikan permainan', 'Mengabaikan bil atau tanggungjawab', 'Berasa tidak mampu berhenti'],
      },
      {
        en_q: 'Is MrCuci intended for minors?',
        bm_q: 'Adakah MrCuci ditujukan kepada bawah umur?',
        en_a: [
          'No. Real-money online casino gambling is intended only for adults aged 18 and above who satisfy applicable legal requirements.',
          'Adults should prevent minors from accessing gambling accounts, payment methods, passwords and devices used for gambling. Use device locks and do not save login credentials on shared devices.',
        ],
        bm_a: [
          'Tidak. Perjudian kasino wang sebenar hanya ditujukan kepada orang dewasa berumur 18 tahun ke atas yang memenuhi syarat undang-undang.',
          'Orang dewasa perlu menghalang bawah umur daripada mengakses akaun, pembayaran, kata laluan dan peranti perjudian. Gunakan kunci peranti dan jangan simpan log masuk pada peranti dikongsi.',
        ],
      },
    ],
  },
];

export default function FAQPage() {
  const { lang, t } = useLanguage();
  const title = t(
    'MrCuci Malaysia Online Casino FAQ | Bonuses, Payments & Games',
    'FAQ Kasino Dalam Talian MrCuci Malaysia | Bonus, Pembayaran & Permainan'
  );
  const description = t(
    'Detailed MrCuci Malaysia FAQ covering registration, slots, live casino, bonuses, MYR deposits, withdrawals, mobile access, account security and responsible gaming.',
    'FAQ terperinci MrCuci Malaysia mengenai pendaftaran, slot, kasino langsung, bonus, deposit MYR, pengeluaran, akses mudah alih, keselamatan akaun dan permainan bertanggungjawab.'
  );

  usePageSeo({
    title,
    description,
    path: '/faq/',
    language: lang === 'bm' ? 'ms-MY' : 'en-MY',
    keywords: [
      'MrCuci FAQ',
      'MrCuci Malaysia FAQ',
      'Malaysia online casino FAQ',
      'MrCuci registration',
      'MrCuci login help',
      'MrCuci bonus terms',
      'MrCuci deposit methods',
      'MrCuci withdrawal guide',
      'MrCuci mobile casino',
      'MrCuci account security',
      'FAQ kasino dalam talian Malaysia',
      'soalan lazim MrCuci',
    ],
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          '@id': 'https://www.mrcuci.me/faq/#breadcrumb',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: t('Home', 'Utama'),
              item: 'https://www.mrcuci.me/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'FAQ',
              item: 'https://www.mrcuci.me/faq/',
            },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': 'https://www.mrcuci.me/faq/#webpage',
          url: 'https://www.mrcuci.me/faq/',
          name: title,
          description,
          inLanguage: lang === 'bm' ? 'ms-MY' : 'en-MY',
          isPartOf: { '@id': 'https://www.mrcuci.me/#website' },
          breadcrumb: { '@id': 'https://www.mrcuci.me/faq/#breadcrumb' },
          mainEntity: faqSections.flatMap((section) =>
            section.items.map((item) => ({
              '@type': 'Question',
              name: t(item.en_q, item.bm_q),
              acceptedAnswer: {
                '@type': 'Answer',
                text: t(item.en_a.join(' '), item.bm_a.join(' ')),
              },
            }))
          ),
        },
      ],
    },
  });

  const totalQuestions = faqSections.reduce((total, section) => total + section.items.length, 0);

  return (
    <div className="min-h-screen bg-[#080000] font-sans">
      <Navbar />

      <main className="pt-20">
        <section
          className="py-14 md:py-20 border-b border-[#d4a017]/10"
          style={{ background: 'linear-gradient(135deg, #0d0000 0%, #1a0303 55%, #0d0000 100%)' }}
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <p className="text-[#d4a017] text-xs font-semibold tracking-widest uppercase mb-4">
              {t('Malaysian Player Guide', 'Panduan Pemain Malaysia')}
            </p>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-5">
              <LinkedText>{t(
                'MrCuci Malaysia Online Casino FAQ – Bonuses, Payments & Games',
                'FAQ Kasino Dalam Talian MrCuci Malaysia – Bonus, Pembayaran & Permainan'
              )}</LinkedText>
            </h1>
            <div className="space-y-4 text-gray-300 text-sm md:text-base leading-[1.85] max-w-3xl">
              <p>
                <LinkedText>{t(
                  'Looking for information about MrCuci Malaysia Online Casino? This detailed FAQ brings together answers about account registration, online casino games, slots, live casino, promotions, MYR deposits and withdrawals, mobile gaming, account security and responsible gaming.',
                  'Mencari maklumat tentang Kasino Dalam Talian MrCuci Malaysia? FAQ terperinci ini menghimpunkan jawapan tentang pendaftaran akaun, permainan kasino, slot, kasino langsung, promosi, deposit dan pengeluaran MYR, permainan mudah alih, keselamatan akaun serta permainan bertanggungjawab.'
                )}</LinkedText>
              </p>
              <p>
                <LinkedText>{t(
                  'Whether you are visiting MrCuci for the first time or already have an account, use this guide to understand how the platform works before you play. Individual games, payment methods and promotions can change, so always confirm current information on the relevant page or inside your account.',
                  'Sama ada anda mengunjungi MrCuci buat kali pertama atau sudah mempunyai akaun, gunakan panduan ini untuk memahami cara platform berfungsi sebelum bermain. Permainan, pembayaran dan promosi boleh berubah, maka sahkan maklumat semasa pada halaman berkaitan atau dalam akaun.'
                )}</LinkedText>
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-[#d4a017]/10" style={{ background: '#0d0101' }}>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-black text-[#d4a017]">{faqSections.length}</p>
              <p className="text-gray-500 text-xs">{t('Detailed topics', 'Topik terperinci')}</p>
            </div>
            <div>
              <p className="text-2xl font-black text-[#d4a017]">{totalQuestions}</p>
              <p className="text-gray-500 text-xs">{t('Questions answered', 'Soalan dijawab')}</p>
            </div>
            <div>
              <p className="text-2xl font-black text-[#d4a017]">18+</p>
              <p className="text-gray-500 text-xs">{t('Adults only', 'Dewasa sahaja')}</p>
            </div>
          </div>
        </section>

        <nav
          aria-label={t('FAQ contents', 'Kandungan FAQ')}
          className="border-b border-[#d4a017]/10"
          style={{ background: '#0b0000' }}
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-7">
            <h2 className="text-white font-black text-lg mb-4">
              {t('Browse the MrCuci FAQ guide', 'Lihat panduan FAQ MrCuci')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {faqSections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-2 rounded-lg border border-[#d4a017]/10 px-4 py-3 text-xs font-semibold text-gray-400 hover:border-[#d4a017]/40 hover:text-[#d4a017] transition-colors"
                >
                  <i className="ri-arrow-right-s-line" />
                  {t(section.en_heading, section.bm_heading)}
                </a>
              ))}
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16 space-y-14">
          {faqSections.map((section, sectionIndex) => (
            <section id={section.id} key={section.id} className="scroll-mt-24">
              <div className="mb-7">
                <p className="text-[#d4a017] text-xs font-black tracking-widest uppercase mb-2">
                  {t('FAQ Topic', 'Topik FAQ')} {String(sectionIndex + 1).padStart(2, '0')}
                </p>
                <h2 className="text-2xl md:text-3xl font-black text-white mb-4">
                  {t(section.en_heading, section.bm_heading)}
                </h2>
                <div className="space-y-3 text-gray-400 text-sm leading-[1.9]">
                  {(lang === 'bm' ? section.bm_intro : section.en_intro).map((paragraph) => (
                    <p key={paragraph}><LinkedText>{paragraph}</LinkedText></p>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {section.items.map((item, itemIndex) => {
                  const paragraphs = lang === 'bm' ? item.bm_a : item.en_a;
                  const points = lang === 'bm' ? item.bm_points : item.en_points;
                  return (
                    <article
                      key={item.en_q}
                      className="rounded-2xl border border-[#d4a017]/10 p-5 md:p-7"
                      style={{ background: 'linear-gradient(135deg, #180303, #1e0404)' }}
                    >
                      <div className="flex items-start gap-3 mb-4">
                        <span
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black text-black shrink-0"
                          style={{ background: 'linear-gradient(135deg, #cc1100, #d4a017)' }}
                        >
                          {itemIndex + 1}
                        </span>
                        <h3 className="text-base md:text-lg font-black text-white leading-snug">
                          {t(item.en_q, item.bm_q)}
                        </h3>
                      </div>
                      <div className="space-y-3 text-gray-400 text-sm leading-[1.85]">
                        {paragraphs.map((paragraph) => (
                          <p key={paragraph}><LinkedText>{paragraph}</LinkedText></p>
                        ))}
                      </div>
                      {points && (
                        <div className="mt-5 pt-4 border-t border-white/5">
                          <h4 className="text-sm font-bold text-[#d4a017] mb-3">
                            {t('Important points to check', 'Perkara penting untuk disemak')}
                          </h4>
                          <ul className="grid sm:grid-cols-2 gap-2 text-gray-400 text-sm">
                            {points.map((point) => (
                              <li key={point} className="flex items-start gap-2">
                                <i className="ri-check-line text-[#d4a017] mt-0.5" />
                                <span><LinkedText>{point}</LinkedText></span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}

          <section className="rounded-2xl border border-[#d4a017]/20 p-6 md:p-8" style={{ background: 'linear-gradient(135deg, #200404, #2a0505)' }}>
            <h2 className="text-2xl font-black text-white mb-4">
              {t('Find more information about MrCuci Malaysia', 'Dapatkan maklumat lanjut tentang MrCuci Malaysia')}
            </h2>
            <div className="space-y-4 text-gray-400 text-sm leading-[1.9] mb-7">
              <p>
                {t(
                  'This guide answers common questions, but individual games, payment methods, promotions and account requirements can change. Review the relevant MrCuci page before depositing, claiming a promotion or playing a casino game.',
                  'Panduan ini menjawab soalan lazim, tetapi permainan, kaedah pembayaran, promosi dan syarat akaun boleh berubah. Semak halaman MrCuci berkaitan sebelum deposit, menuntut promosi atau bermain permainan kasino.'
                )}
              </p>
              <p>
                {t(
                  'Gambling always involves financial risk. Set your own limits, understand the rules of the games you choose and treat casino gaming as entertainment rather than a source of guaranteed income.',
                  'Perjudian sentiasa melibatkan risiko kewangan. Tetapkan had sendiri, fahami peraturan permainan pilihan dan anggap permainan kasino sebagai hiburan, bukan sumber pendapatan terjamin.'
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/bonus/" className="inline-flex items-center gap-2 rounded-lg border border-[#d4a017]/40 px-5 py-3 text-sm font-bold text-[#d4a017]">
                {t('Current Promotions', 'Promosi Semasa')}
              </Link>
              <Link to="/terms/" className="inline-flex items-center gap-2 rounded-lg border border-[#d4a017]/40 px-5 py-3 text-sm font-bold text-[#d4a017]">
                {t('Terms & Conditions', 'Terma & Syarat')}
              </Link>
              <Link to="/responsible-gaming/" className="inline-flex items-center gap-2 rounded-lg border border-[#d4a017]/40 px-5 py-3 text-sm font-bold text-[#d4a017]">
                {t('Responsible Gaming', 'Permainan Bertanggungjawab')}
              </Link>
              <RegisterLink
                source="faq_page_register"
                label={t('Join MrCuci', 'Sertai MrCuci')}
                className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #cc1100, #ff2200)' }}
              >
                {t('Join MrCuci', 'Sertai MrCuci')}
              </RegisterLink>
            </div>
            <p className="mt-6 text-xs font-bold text-gray-500">
              {t('18+ only. Please gamble responsibly.', '18+ sahaja. Sila berjudi secara bertanggungjawab.')}
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
